import { useContext, useEffect, useMemo } from "react";
import { NextPage } from "next";

// Components
import Header from "components/Header";
import Hero from "components/Hero";
import How from "components/How";
import SupportedNetworks from "components/SupportedNetworks";
import Security from "components/Security";
import Partners from "components/Partners";
import Blogs from "components/Blogs";
import Newsletter from "components/Newsletter";
import Footer from "components/Footer";

// Providers
import { PageContext, PageProvider } from "providers/PageProvider";

// Utils
import {
  // fetchPageData,
  getSectionDataByKey,
  prepareNavData,
} from "utils/common";
import { trackEvent } from "@utils/firebase";

// Hooks
import useStaderTVL from "hooks/useStaderTVL";
import Head from "next/head";

const Home = () => {
  const { page } = useContext(PageContext);
  const { tvl } = useStaderTVL();

  const { navData, networks } = useMemo(
    () => prepareNavData(page.data.navData, tvl),
    [page.data.navData, tvl]
  );
  const pageData = page.data.pageData;

  const heroData = pageData.hero;
  const bannerData = pageData.banner;

  const howItWorksData = getSectionDataByKey(pageData, "how_it_works");
  const supportedNetworksData = getSectionDataByKey(pageData, "networks");
  const securityData = getSectionDataByKey(pageData, "security");
  const partnerData = getSectionDataByKey(pageData, "partners");
  const blogsData = getSectionDataByKey(pageData, "blogs");
  const subscriptionData = getSectionDataByKey(pageData, "subscription");

  useEffect(() => {
    const navItems = pageData.sections.map(
      (section: any) =>
        section?.section_details?.section_id ||
        section?.sectionDetails?.section_id
    );

    const mainElements = navItems.map((id: string) =>
      document.getElementById(id)
    );

    const observer = new IntersectionObserver(
      (entries) => {
        for (let entry of entries) {
          if (entry.isIntersecting) {
            trackEvent(`VIEW_${entry.target.id}`);
          }
        }
      },
      {
        threshold: 0.5,
      }
    );

    mainElements.forEach(
      (element: Element) => element && observer.observe(element)
    );

    // cleanup function
    return () => {
      mainElements.forEach(
        (element: Element) => element && observer.unobserve(element)
      );
    };
  }, [pageData.sections]);

  return (
    <>
      <Head>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.staderlabs.com/" />
      </Head>
      <Header
        data={navData}
        networks={networks}
        banner={bannerData}
        tvl={tvl}
      />
      <Hero data={heroData} tvl={tvl} />
      <How data={howItWorksData} />
      <SupportedNetworks
        data={supportedNetworksData}
        networks={networks}
        tvl={tvl}
      />
      <Security data={securityData} />
      <Partners data={partnerData} />
      <Blogs data={blogsData} />
      <Newsletter data={subscriptionData} networks={networks} />
      <Footer data={navData} />
    </>
  );
};

const HomePage: NextPage = ({ page }: any) => {
  return (
    <PageProvider page={page}>
      <Home />
    </PageProvider>
  );
};

// export async function getStaticProps() {
//   const [navigationData, pageRes] = await Promise.all([
//     fetchPageData("landing-navigation"),
//     fetchPageData("landing-v2"),
//   ]);

//   const navData = navigationData.data?.attributes || null;
//   const navError = navigationData.error || null;

//   const pageData = pageRes.data?.attributes || null;
//   const pageError = pageRes.error || null;

//   return {
//     props: {
//       page: {
//         data: {
//           navData: navData,
//           pageData: pageData,
//         },
//         error: { navError, pageError },
//       },
//     },
//   };
// }

export default HomePage;
